/**
 * Energy Flow diagram per docs/implementation/ENERGY_FLOW_VISUALIZATION.md.
 * Layout from useEnergyFlowLayout (Solar-Led / Grid-Led / Battery-Led). Grid always "Grid Import" or "Grid Export" with positive kW.
 */

import { Sun, Battery, Home, Zap } from 'lucide-react';
import { useEnergyFlowLayout } from '@/hooks/useEnergyFlowLayout';
import type { EnergyFlowNode, FlowNodeId } from '@/hooks/useEnergyFlowLayout';

interface EnergyFlowProps {
  /** Solar power in kW */
  solarKw: number;
  /** Battery power in kW (positive = charging, negative = discharging) */
  batteryKw: number;
  /** Load power in kW */
  loadKw: number;
  /** Grid power in kW (positive = export, negative = import); null if disconnected */
  gridKw: number | null;
  /** Generator power in kW (null if offline) */
  genKw: number | null;
  /** When set (e.g. from API flow flags), use for battery label instead of sign of batteryKw */
  batteryStateOverride?: 'Charging' | 'Discharging' | 'Idle';
}

function getFlowKwBetween(flows: { from: FlowNodeId; to: FlowNodeId; kw: number }[], from: FlowNodeId, to: FlowNodeId): number {
  const f = flows.find((e) => e.from === from && e.to === to);
  return f?.kw ?? 0;
}

export function EnergyFlow({
  solarKw,
  batteryKw,
  loadKw,
  gridKw,
  genKw,
  batteryStateOverride,
}: EnergyFlowProps) {
  const layout = useEnergyFlowLayout({
    pvKw: solarKw,
    batteryKw,
    gridKw,
    loadKw,
    genKw,
    batteryStateOverride,
  });

  const mainRowNodes = layout.nodes.filter((n) => n.visible && (n.id === 'solar' || n.id === 'battery' || n.id === 'home' || n.id === 'grid-import' || n.id === 'grid-export'));

  return (
    <div className="rounded-lg border border-border bg-wt-bg-secondary p-4 w-full max-w-[450px] overflow-hidden min-h-[140px] flex flex-col">
      {/* Header — no net value; dominant state lives in Energy Position card */}
      <div className="flex justify-between items-center mb-3">
        <span className="label text-wt-text-secondary">Energy Flow</span>
      </div>

      {/* Flow Diagram — left-to-right from layout.nodes */}
      <div className="flex items-center justify-between gap-4 sm:gap-5 mb-3 flex-nowrap min-w-0">
        {mainRowNodes.map((node, idx) => (
          <div key={node.id} className="contents">
            {idx > 0 && (
              <ArrowSegment
                kw={getFlowKwBetween(layout.flows, mainRowNodes[idx - 1].id, node.id)}
                color={layout.flows.find((f) => f.from === mainRowNodes[idx - 1].id && f.to === node.id)?.color ?? 'border'}
              />
            )}
            <FlowNodeView
              node={node}
              batteryLabel={layout.batteryLabel}
              homeZeroLoadTooltip={
                node.id === 'home' && node.kw === 0 && (solarKw > 0.05 || (gridKw != null && Math.abs(gridKw) > 0.05) || Math.abs(batteryKw) > 0.05)
                  ? 'Inverter reported 0 W load; system is active so some consumption is expected.'
                  : undefined
              }
              homeZeroLoadDisplay={node.id === 'home' && node.kw === 0 && (solarKw > 0.05 || (gridKw != null && Math.abs(gridKw) > 0.05) || Math.abs(batteryKw) > 0.05)}
            />
          </div>
        ))}
      </div>

      {/* Footer: Grid Import / Grid Export only (positive kW); never signed grid */}
      <div className="flex gap-4 pt-3 border-t border-border flex-shrink-0 text-sm">
        <div className="text-sm text-wt-text-secondary">
          <span className="font-medium">
            {layout.gridLabel ? `${layout.gridLabel}:` : gridKw !== null ? 'Grid:' : 'Grid:'}
          </span>{' '}
          <span className="font-mono-numeric">
            {layout.gridLabel ? `${layout.gridKwDisplay.toFixed(1)} kW` : gridKw !== null ? `${layout.gridKwDisplay.toFixed(1)} kW` : 'Disconnected'}
          </span>
        </div>
        {layout.layoutType === 'grid-led' && solarKw > 0 && (
          <div className="text-sm text-wt-text-secondary">
            <span className="font-medium">Solar:</span>{' '}
            <span className="font-mono-numeric">{solarKw.toFixed(1)} kW</span>
          </div>
        )}
        <div className="text-sm text-wt-text-secondary">
          <span className="font-medium">Gen:</span>{' '}
          <span className="font-mono-numeric">{genKw != null ? `${genKw.toFixed(1)} kW` : 'Offline'}</span>
        </div>
      </div>
    </div>
  );
}

function ArrowSegment({ kw, color }: { kw: number; color: string }) {
  const active = kw >= 0.1;
  const colorClass = color === 'wt-green-primary' ? 'bg-wt-green-primary' : color === 'destructive' ? 'bg-destructive' : color === 'wt-amber-primary' ? 'bg-wt-amber-primary' : 'bg-border';
  const borderClass = color === 'wt-green-primary' ? 'border-l-wt-green-primary' : color === 'destructive' ? 'border-l-destructive' : color === 'wt-amber-primary' ? 'border-l-wt-amber-primary' : 'border-l-border';
  return (
    <div className="flex items-center flex-shrink-0 min-w-[24px] max-w-[48px]">
      <div className={`h-[3px] w-8 flex-1 min-w-[24px] relative transition-all duration-300 ${active ? colorClass : 'bg-border'}`}>
        {active && (
          <div className={`absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-l-[6px] ${borderClass} border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent`} />
        )}
      </div>
    </div>
  );
}

function FlowNodeView({
  node,
  batteryLabel,
  homeZeroLoadTooltip,
  homeZeroLoadDisplay,
}: {
  node: EnergyFlowNode;
  batteryLabel: 'Charging' | 'Discharging' | 'Idle';
  homeZeroLoadTooltip?: string;
  homeZeroLoadDisplay?: boolean;
}) {
  const isBattery = node.id === 'battery';
  const isSolar = node.id === 'solar';
  const isHome = node.id === 'home';
  const isGridImport = node.id === 'grid-import';

  const Icon = isSolar ? Sun : isBattery ? Battery : isHome ? Home : Zap;
  const active = node.kw > 0.1 || isBattery || (isHome && homeZeroLoadDisplay);
  const borderColor =
    node.color === 'wt-green-primary' ? 'border-wt-green-primary' :
    node.color === 'destructive' ? 'border-destructive' :
    node.color === 'wt-amber-primary' ? 'border-wt-amber-primary' :
    node.color === 'wt-solar-gold' ? 'border-wt-solar-gold' :
    'border-border';
  const textColor =
    node.color === 'wt-green-primary' ? 'text-wt-green-primary' :
    node.color === 'destructive' ? 'text-destructive' :
    node.color === 'wt-amber-primary' ? 'text-wt-amber-primary' :
    node.color === 'wt-solar-gold' ? 'text-wt-solar-gold' :
    'text-wt-text-secondary';
  const label = isBattery ? batteryLabel : node.label;
  const valueLabel = isBattery
    ? label
    : isHome && homeZeroLoadDisplay
      ? '~0 kW'
      : active
        ? `${node.kw.toFixed(1)} kW`
        : '—';
  const title = isHome && homeZeroLoadTooltip ? homeZeroLoadTooltip : (!isBattery ? `${node.kw.toFixed(1)} kW` : undefined);

  return (
    <div className={`flex flex-col items-center gap-1.5 flex-shrink-0 min-w-[52px] ${node.dimmed ? 'opacity-60' : ''}`}>
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
          active ? `bg-wt-bg-tertiary ${borderColor}` : 'bg-wt-bg-tertiary border-border'
        }`}
      >
        <Icon
          className={`w-5 h-5 ${active ? textColor : 'text-wt-text-secondary'}`}
        />
      </div>
      <span className="label text-wt-text-secondary">{node.label}</span>
      <span
        className={`font-mono-numeric text-[13px] font-medium truncate max-w-[72px] text-center ${
          active ? (isBattery ? textColor : 'text-foreground font-semibold') : 'text-wt-text-secondary'
        }`}
        title={title}
      >
        {valueLabel}
      </span>
    </div>
  );
}
